import {ApplicationRef, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Datacenter} from "@core/model/datacenter";
import { SourceDataType } from '@core/ngrx/actions/datacenter.actions';
import * as ProviderActions from '@core/ngrx/actions/provider.actions';
import {MapConfigSelector} from "@core/ngrx/selectors/map-config.selector";
import { ProvidersSelector } from '@core/ngrx/selectors/providers.selector';
import {Store} from "@ngrx/store";
import {Subject, takeUntil, tap} from 'rxjs';
import {geoActions, providersActions, datacenterActions} from "@core/ngrx/actions";
import {Actions, ofType} from "@ngrx/effects";
import {GeoInfo} from "@core/model/geo-info";
import * as _ from "lodash";
import {Provider} from "@core/model/providers";
import {DatacenterV2} from "@core/model/datacenter-v2";

type DatacenterProvider = {
  backgroundImage: string;
  color: string;
};
@Component({
  selector: 'app-providers-datacenters-details',
  templateUrl: './providers-datacenters-details.component.html',
  styleUrls: ['./providers-datacenters-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ProvidersDatacentersDetailsComponent implements OnInit, OnDestroy {

  public providers: Provider[] = [];
  public datacenters: DatacenterV2[] = [];
  public providersBack: Provider[] = [];
  public currentFilter: string[] = [];
  protected datacenterProviders: Map<string, DatacenterProvider> = new Map<string, DatacenterProvider>();
  protected polygonsSourceData: Map<string, SourceDataType> = new Map<string, SourceDataType>();
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private mapConfigSelector: MapConfigSelector,
              private readonly store: Store,
              private actions$: Actions,
              private appRef: ApplicationRef,
              private providersSelector: ProvidersSelector) {
  }

  ngOnInit(): void {
    this.listenLoadGLobalProvidersSuccess();
    this.actions$.pipe(
      ofType(datacenterActions.GET_DATACENTERS_BY_PROVIDER_SUCCESS),
      takeUntil(this.destroy$),
      tap((data: any) => {
        const provider = _.cloneDeep(data.provider);
        provider.datacenters = _.cloneDeep(data.datacenters);
        if (data.sourceData) {
          this.polygonsSourceData.set(provider.id, data.sourceData);
        }
        this.providers.push(provider)
      })
    ).subscribe();

    this.actions$.pipe(
      ofType(providersActions.providerUnselected),
      takeUntil(this.destroy$),
      tap((data: any) => {
        const provider = _.cloneDeep(data.provider);
        this.providers = this.providers.filter(p => p.id !== provider.id);
      })
    ).subscribe();

    this.actions$.pipe(
      ofType(providersActions.providerColorChanged),
      takeUntil(this.destroy$),
      tap((providerAction) => {
        const providedChanged = this.providers.find(p => p.id === providerAction.provider.id);
        if (providedChanged) {
          providedChanged.color = providerAction.provider.color;
          this.providers = this.providers.filter(p => p.id !== providerAction.provider.id);
          this.providers.push(providedChanged);
        }
      })
    ).subscribe();

    this.actions$.pipe(
      ofType(providersActions.providersModified),
      takeUntil(this.destroy$),
      tap((providerAction) => {
        const providedChanged = this.providers.find(p => p.id === providerAction.provider.id);
        providedChanged.base64Icon = providerAction.provider.base64Icon
        this.providers = this.providers.filter(p => p.id !== providerAction.provider.id);
        this.providers.push(providedChanged)
      })
    ).subscribe();

  }

  private listenLoadGLobalProvidersSuccess(): void {
    this.actions$
      .pipe(
        ofType(ProviderActions.loadGlobalProvidersSuccess.type),
        takeUntil(this.destroy$),
        tap((action: any) => {
          this.providersSelector.getAllProviders().pipe(
            takeUntil(this.destroy$),
            tap(providers => {
              const globalProviders = action.response.data;
              const providersMap = new Map(
                providers.map(p => {
                  const globalProvider = globalProviders.find(gp => gp.name.toLowerCase() === p.name.toLowerCase());
                  let backgroundImage = 'none';
                  if (globalProvider) {
                    backgroundImage = `url(data:image/${globalProvider.extension};base64,${globalProvider.base64Icon})`
                  }
                  if (p.base64Icon) {
                    backgroundImage = `url(${p.base64Icon})`;
                  }
                  const value: DatacenterProvider = {
                    color: p.color,
                    backgroundImage,
                  }
                  return [p.name, value];
                })
              );
              this.datacenterProviders = providersMap;
            })
          ).subscribe();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  getDatacentersByProvider(provider: Provider) {
    return this.datacenters.filter(dc => dc.provider == provider.name);
  }

  goToLocation(building: Datacenter) {
    const geoInfo: GeoInfo = {
      desc: "", id: "",
      name: "",
      latitude: building.lat,
      longitude: building.lon,
      zoom: 12
    };
    this.store.dispatch(geoActions.centerMap({info: geoInfo}));
  }
}
